import React from "react";
import { Box, FormControlLabel, formControlLabelClasses, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import StarIcon from "@mui/icons-material/Star";
import { sliderClasses } from "@mui/material/Slider";

import MzTooltip from "@markaaz/ui/MzTooltip";
import MzSlider from "@markaaz/ui/MzSlider";
import MzCheckbox from "@markaaz/ui/MzCheckbox";
import { MzRadioGroup, MzRadio } from "@markaaz/ui/MzRadioControls";
import { scaleUpCurrency, scaleDownCurrency, valueLabelFormat } from "./slider";

const sxRow = {
  root: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "15px 20px 15px 8px",
    color: "darkBlue.c100",
    borderBottom: (theme) => `1px solid ${theme.palette.blue.c20}`,
    [`.${formControlLabelClasses.root} .${formControlLabelClasses.label}.Mui-disabled`]: {
      color: "darkBlue.c100"
    },
    "&:last-child": {
      border: "none"
    }
  },
  label: {
    fontWeight: 600
  },
  icon: {
    color: "lightBlue.c100"
  },
  premium: {
    backgroundColor: "darkBlue.c10",
    borderBottom: (theme) => `1px solid ${theme.palette.common.white}`,
    height: "40px"
  },
  slider: {
    height: "90px",

    [`.${sliderClasses.root}`]: {
      width: "402px",
      marginTop: "20px",
      marginRight: "40px"
    }
  },
  required: {
    color: "negative.c100"
  },
  risk: {
    [`.${sliderClasses.rail}`]: {
      background: (theme) => theme.palette.common.gradientRiskReverse,
      transform: "rotate(-180deg)",
      top: "44%"
    }
  },
  noMarginLeft: {
    marginLeft: 0
  },
  financial: {
    [`.${sliderClasses.track}`]: {
      background: (theme) => theme.palette.lightBlue.c100
    },
    [`.${sliderClasses.rail}`]: {
      background: (theme) => theme.palette.blue.c20
    }
  }
};

export default function DecisioningRow({ rowData, values, onRadioChange, onCheckboxChange, onSliderChange }) {
  const label = rowData.label;
  const options = rowData.options || [];
  const tooltip = rowData.tooltipContent;
  const SelectedValues = values[rowData.radioKey];
  const isChecked = values[rowData.checkboxKey] || false;
  const isPremiumUser = values.isPremium;
  const isPremiumService = rowData.premium;
  const canShowCheckbox = isPremiumService ? isPremiumUser : rowData.checkboxKey === false ? false : true;
  const classes = [sxRow.root];
  const defaultMinDistance = 1;

  if (rowData._do_not_render) return;

  if ((!isPremiumUser && isPremiumService) || rowData.checkboxKey === false) {
    classes.push(sxRow.premium);
  }

  if (rowData.slider || (!isPremiumUser && isPremiumService)) {
    classes.push(sxRow.slider);
  }

  const labelComponent = !rowData.required ? (
    <Typography variant="body2" sx={sxRow.label}>
      {label}
    </Typography>
  ) : (
    <Box sx={{ display: "inline-flex" }}>
      <Typography variant="body2" sx={sxRow.label}>
        {label}
      </Typography>
      <Typography sx={sxRow.required}>*</Typography>
    </Box>
  );

  function handleScale(value) {
    return getScaleUpValue(value);
  }

  function getScaleUpValue(value) {
    if (rowData.slider.scale === "currency") {
      return scaleUpCurrency(rowData.slider.marks, value);
    } else {
      return value * -1;
    }
  }

  function getScaleDownValue(value) {
    if (rowData.slider.scale === "currency") {
      return scaleDownCurrency(rowData.slider.marks, value);
    } else {
      return value * -1;
    }
  }

  let initialSliderValue = 0;
  if (rowData.slider) {
    if (Array.isArray(rowData.slider.valueKey)) {
      let initialSliderValues = [];
      rowData.slider.valueKey.forEach((value) => {
        if (isNaN(values[value])) {
          initialSliderValues.push(0);
        } else {
          initialSliderValues.push(values[value]);
        }
      });
      initialSliderValue = initialSliderValues;
    } else {
      const value = Number(values[rowData.slider.valueKey]);

      if (isNaN(value)) {
        initialSliderValue = 0;
      } else {
        initialSliderValue = value;
      }

      if (rowData.slider.scale) {
        initialSliderValue = getScaleDownValue(initialSliderValue);
      }
    }
  }

  function handleSliderChange(_, value, activeThumb) {
    if (Array.isArray(rowData.slider.valueKey)) {
      let newValue = value;
      let minDistance = rowData.slider?.minDistance ? rowData.slider?.minDistance : defaultMinDistance;
      if (activeThumb === 0) {
        newValue = [Math.min(newValue[0], value[1] - minDistance), value[1]];
        onSliderChange(_, [Math.min(newValue[0], value[1] - minDistance), value[1]]);
      } else {
        newValue = [Math.min(newValue[0], value[1] - minDistance), value[1]];
        onSliderChange(_, [value[0], Math.max(newValue[1], value[0] + minDistance)]);
      }
    } else {
      let newValue = value;
      if (rowData.slider.scale) {
        newValue = getScaleUpValue(value);
      }
      onSliderChange(_, newValue);
    }
  }

  return (
    <Box sx={classes}>
      {canShowCheckbox && (
        <Box>
          <FormControlLabel
            sx={sxRow.noMarginLeft}
            control={
              <MzCheckbox
                sx={sxRow.noMarginLeft}
                checked={isChecked}
                disabled={rowData.required}
                onChange={onCheckboxChange}
              />
            }
            label={labelComponent}
          />
          {rowData.required && <Required />}
        </Box>
      )}

      {!canShowCheckbox && labelComponent}

      <Box flexGrow={1}></Box>

      {rowData.slider && canShowCheckbox && (
        <MzSlider
          value={initialSliderValue}
          onChange={handleSliderChange}
          min={rowData.slider.min}
          max={rowData.slider.max}
          step={rowData.slider.step}
          marks={rowData.slider.marks}
          scale={rowData.slider.scale && handleScale}
          valueLabelFormat={rowData.slider.valueLabelFormat && valueLabelFormat(rowData.slider.valueLabelFormat)}
          track={rowData.slider.track}
          sx={sxRow[rowData.slider.sx]}
        />
      )}

      {!rowData.slider && (
        <MzRadioGroup row chip value={SelectedValues} onChange={onRadioChange}>
          {options.map((value) => {
            return <MzRadio disabled={!isChecked} key={value} value={value} />;
          })}
        </MzRadioGroup>
      )}

      {!isPremiumUser && isPremiumService && <PremiumService />}

      <MzTooltip
        title={tooltip}
        arrow
        PopperProps={{
          modifiers: [{ name: "preventOverflow", options: { padding: 20 } }]
        }}
      >
        <InfoOutlinedIcon sx={sxRow.icon}></InfoOutlinedIcon>
      </MzTooltip>
    </Box>
  );
}

const sxPremium = {
  display: "flex",
  gap: "4px",
  marginRight: "12px",
  textTransform: "uppercase",
  color: "blue.c100",
  svg: {
    fontSize: "13px"
  }
};

function PremiumService() {
  return (
    <Box sx={sxPremium}>
      <StarIcon />
      <Typography variant="overline1">Premium Service</Typography>
    </Box>
  );
}

const sxRequired = {
  color: "negative.c100",
  marginLeft: "30px"
};

function Required() {
  return (
    <Box sx={sxRequired}>
      <Typography variant="body2">*Required</Typography>
    </Box>
  );
}
